import styled from "@emotion/styled";
import { Button, DatePicker, Input, Select, Space } from "antd";
import dayjs from "dayjs";
import { FC, SetStateAction, useEffect, useState } from "react";
import type { PartnerTransportBrand } from "services/interface";

export type ParcelFilterOption = { value: string; label: string };

interface ParcelSearchFormProps {
  searchInput: string;
  setSearchInput: (value: SetStateAction<string>) => void;
  dateInput: string;
  setDateInput: (value: SetStateAction<string>) => void;
  onSearch?: () => void;
  onClear?: () => void;
  viewpoint?: string;
  onViewpointChange?: (value: string) => void;
  viewpointOptions?: ParcelFilterOption[];
  /** รายการแบรนด์จาก GET /factory/orders/transport-brands */
  brandList?: PartnerTransportBrand[];
  selectedBrandCodes?: string[];
  setSelectedBrandCodes?: (value: string[] | ((prev: string[]) => string[])) => void;
}

/** ค่า brand ที่ใช้เป็น value ใน Select (brand_code หรือ id) */
const getBrandValue = (b: PartnerTransportBrand) =>
  b.brand_code != null && b.brand_code !== "" ? b.brand_code : b.id;

const FIELD_HEIGHT_STYLE = { height: "32px" } as const;

/** ความกว้าง dropdown แบรนด์ขนส่ง / สถานะ — ใช้ค่าเดียวกัน */
const BRAND_SELECT_WIDTH = 200;

const ParcelSearchForm: FC<ParcelSearchFormProps> = ({
  searchInput,
  setSearchInput,
  dateInput,
  setDateInput,
  onSearch,
  onClear,
  viewpoint,
  onViewpointChange,
  viewpointOptions = [],
  brandList = [],
  selectedBrandCodes = [],
  setSelectedBrandCodes,
}) => {
  const [tempSearchInput, setTempSearchInput] = useState<string>(searchInput);
  const [tempDateInput, setTempDateInput] = useState<string>(dateInput);

  useEffect(() => {
    setTempSearchInput(searchInput);
  }, [searchInput]);

  useEffect(() => {
    setTempDateInput(dateInput);
  }, [dateInput]);

  const handleSearch = () => {
    setSearchInput(tempSearchInput);
    setDateInput(tempDateInput);
    onSearch?.();
  };

  const handleClear = () => {
    setTempSearchInput("");
    setTempDateInput("");
    setSearchInput("");
    setDateInput("");
    if (setSelectedBrandCodes) setSelectedBrandCodes([]);
    onViewpointChange?.("all");
    onClear?.();
  };

  const handleDateChange = (date: unknown) => {
    setTempDateInput(date ? dayjs(date as dayjs.Dayjs).format("YYYY-MM-DD") : "");
  };

  const brandOptions = brandList.map((b) => ({
    value: getBrandValue(b),
    label: b.brand_name,
  }));

  const showClearBtn =
    !!tempSearchInput ||
    !!tempDateInput ||
    selectedBrandCodes.length > 0 ||
    (viewpoint != null && viewpoint !== "all");

  return (
    <ButtonWrapper>
      <Space.Compact>
        <Select
          mode="multiple"
          style={{ minWidth: BRAND_SELECT_WIDTH, width: BRAND_SELECT_WIDTH, ...FIELD_HEIGHT_STYLE }}
          placeholder="แบรนด์ (ทั้งหมด)"
          value={selectedBrandCodes}
          onChange={(v) => setSelectedBrandCodes?.(v ?? [])}
          options={brandOptions}
          allowClear
          maxTagCount="responsive"
        />
        <Input
          style={FIELD_HEIGHT_STYLE}
          placeholder="เลขพัสดุ / เลขอ้างอิง"
          value={tempSearchInput}
          onChange={(e) => setTempSearchInput(e.target.value)}
          onPressEnter={handleSearch}
        />
        <DatePicker
          style={{ width: "100%" }}
          placeholder="วันที่สร้าง"
          format="DD/M/YYYY"
          placement="topRight"
          showToday
          inputReadOnly
          value={tempDateInput ? dayjs(tempDateInput) : null}
          onChange={handleDateChange}
        />
        {viewpointOptions.length > 0 && onViewpointChange ? (
          <Select
            style={{ width: "100%", ...FIELD_HEIGHT_STYLE }}
            value={viewpoint}
            onChange={onViewpointChange}
            options={viewpointOptions}
          />
        ) : null}
        <Button type="default" onClick={handleSearch}>
          ค้นหา
        </Button>
        {showClearBtn ? (
          <Button type="default" onClick={handleClear}>
            X
          </Button>
        ) : null}
      </Space.Compact>
    </ButtonWrapper>
  );
};

export default ParcelSearchForm;

/** รูปแบบเดียวกับ OrderTabsCard/SearchOrderForm */
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;
